/**
 * Ekranı paylaş: o anki süzgeç / arama / sıralama bir bağlantıya konur ve seçilen kişilere
 * (ya da gruplara) mesaj kartı olarak gönderilir. Kartı açan kullanıcı ekrana yetkiliyse ekran
 * aynı süzgeçle açılır; veri yine onun KENDİ yetkileriyle gelir. Sohbet yetkisi yoksa yalnız
 * bağlantı kopyalanır.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Link2, Search, Users } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Textarea } from '@/shared/ui/Textarea';
import { Avatar } from '@/shared/ui/Avatar';
import { toast } from '@/shared/ui/toastStore';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useUserPhoto } from '@/features/presence/model/presenceHooks';
import { shareUrl, MAX_SHARE_PARAM, type GridShareState } from '@/features/grid/model/shareState';
import { useChatStore } from '../model/chatStore';
import type { SharePayload } from '../model/types';

interface Target {
  key: string;
  title: string;
  userId?: string;
  group: boolean;
  hint?: string | null;
}

function TargetRow({ t: target, checked, onToggle }: { t: Target; checked: boolean; onToggle: () => void }) {
  const photo = useUserPhoto(target.userId ?? '');
  return (
    <label className="hover:bg-bg-hover flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      {target.group ? (
        <span className="bg-brand-500/15 text-brand-600 flex h-7 w-7 items-center justify-center rounded-full">
          <Users className="h-3.5 w-3.5" />
        </span>
      ) : (
        <Avatar name={target.title} imageBase64={photo} className="h-7 w-7 text-[10px]" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{target.title}</span>
        {target.hint ? <span className="text-fg-subtle block truncate text-[11px]">{target.hint}</span> : null}
      </span>
    </label>
  );
}

export function ShareScreenDialog({
  screenId,
  caption,
  state,
  summary,
  onClose,
}: {
  screenId: string;
  caption: string;
  state: GridShareState;
  summary: { filters: number; search: string; rows: number };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canUseChat = useAuthStore((s) => !!s.user?.canUseChat);
  const conversations = useChatStore((s) => s.conversations);
  const contacts = useChatStore((s) => s.contacts);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!canUseChat) return;
    const s = useChatStore.getState();
    if (!s.conversationsLoaded) void s.loadConversations();
    void s.loadContacts();
  }, [canUseChat]);

  const url = shareUrl(screenId, state);
  const tooLong = url.length > MAX_SHARE_PARAM;
  const summaryText = [
    summary.filters ? t('chat.share.filters', { count: summary.filters }) : null,
    summary.search.trim() ? t('chat.share.search', { text: summary.search.trim() }) : null,
    t('chat.share.rows', { count: summary.rows }),
  ]
    .filter(Boolean)
    .join(' · ');

  // Önce son konuşmalar (grup dahil), ardından konuşma geçmişi olmayan kişiler.
  const targets = useMemo<Target[]>(() => {
    const seen = new Set<string>();
    const out: Target[] = [];
    for (const c of conversations) {
      seen.add(c.key);
      out.push({ key: c.key, title: c.title ?? '', userId: c.peerUserId, group: c.kind === 'group' });
    }
    for (const c of contacts) {
      const key = `u:${c.userId}`;
      if (seen.has(key)) continue;
      out.push({ key, title: c.userName, userId: c.userId, group: false, hint: c.roleName });
    }
    const term = q.trim().toLocaleLowerCase('tr');
    return term ? out.filter((x) => x.title.toLocaleLowerCase('tr').includes(term)) : out;
  }, [conversations, contacts, q]);

  const copy = async () => {
    await navigator.clipboard.writeText(window.location.origin + url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const send = async () => {
    setBusy(true);
    const payload: SharePayload = { v: 1, screenId, caption, url, summary: summaryText, rowCount: summary.rows };
    let ok = 0;
    for (const key of picked) {
      const saved = await useChatStore.getState().send(key, { content: note.trim(), type: 'share', payload: JSON.stringify(payload) });
      if (saved) ok++;
    }
    setBusy(false);
    if (ok === picked.length) {
      toast.success(t('chat.share.sent', { count: ok }));
      onClose();
    } else toast.error(t('chat.sendFailed'));
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('chat.share.title', { caption })}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => void copy()} disabled={tooLong}>
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {copied ? t('chat.share.copied') : t('chat.share.copyLink')}
          </Button>
          {canUseChat ? (
            <Button disabled={picked.length === 0 || busy || tooLong} onClick={() => void send()}>
              {t('chat.send')} {picked.length ? `(${picked.length})` : ''}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="bg-bg-muted rounded-lg px-3 py-2 text-xs">
          <span className="block font-semibold">{caption}</span>
          <span className="text-fg-muted">{summaryText}</span>
          <span className="text-fg-subtle mt-1 block">{t('chat.share.permissionHint')}</span>
        </div>
        {tooLong ? <p className="text-danger text-xs">{t('chat.share.tooLong')}</p> : null}
        {canUseChat ? (
          <>
            <div className="relative">
              <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('chat.searchPeople')}
                className="border-border bg-bg h-9 w-full rounded-md border pr-2 pl-8 text-sm outline-none"
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {targets.map((x) => (
                <TargetRow
                  key={x.key}
                  t={x}
                  checked={picked.includes(x.key)}
                  onToggle={() => setPicked((p) => (p.includes(x.key) ? p.filter((k) => k !== x.key) : [...p, x.key]))}
                />
              ))}
              {targets.length === 0 ? <p className="text-fg-subtle py-4 text-center text-sm">{t('chat.noPeople')}</p> : null}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('chat.share.note')} rows={2} maxLength={1000} />
          </>
        ) : (
          <p className="text-fg-muted text-xs">{t('chat.share.noChat')}</p>
        )}
      </div>
    </Dialog>
  );
}
